import { gsap, ScrollTrigger } from "../global/gsap";
import vars from "../global/vars.js";
import initLazyVideo from "../utilities/lazy-video";
import initLoadIn from "../utilities/load-in";
import loadYouTubeApi from "../utilities/youtube-api";

function initContent(content) {
    const loadInEls = content.querySelectorAll(".js-load-in");
    const lazyVideoEls = content.querySelectorAll("video.js-lazy");

    if (loadInEls.length) {
        initLoadIn(loadInEls);
    }

    if (lazyVideoEls.length) {
        initLazyVideo(lazyVideoEls);
    }
}

let activeController = null;

function getYouTubeId(urlStr) {
    try {
        // Quick trim to prevent whitespace errors
        const url = new URL(urlStr.trim());

        // 1. Handle shortened URLs first (e.g., youtu.be/BJCE_7NPD7k?si=123)
        if (url.hostname === "youtu.be" || url.hostname === "www.youtu.be") {
            // pathname usually looks like "/BJCE_7NPD7k". Split by "/" and filter out empties.
            const parts = url.pathname.split('/').filter(Boolean);
            return parts[0] || null;
        }

        // 2. Handle standard watch URLs (e.g., youtube.com/watch?v=...)
        if (url.searchParams.has("v")) {
            return url.searchParams.get("v");
        }

        // 3. Handle embed URLs (e.g., youtube.com/embed/BJCE_7NPD7k)
        if (url.pathname.startsWith("/embed/")) {
            return url.pathname.split("/")[2] || null;
        }

        // 4. Handle shorts URLs (e.g., youtube.com/shorts/BJCE_7NPD7k)
        if (url.pathname.startsWith("/shorts/")) {
            return url.pathname.split("/")[2] || null;
        }

        return null;
    } catch (error) {
        console.error("Invalid URL provided:", error.message);
        return null;
    }
}

async function initYoutube(target, url) {
    const YT = await loadYouTubeApi();
    let videoId = getYouTubeId(url);

    const player = new YT.Player(target, {
        videoId
    });
}

async function fetchContent(
    href,
    fetchContainer,
    fetchDestination,
    closeFetchButton,
    loader,
) {
    if (activeController) {
        activeController.abort();
    }

    activeController = new AbortController();

    vars.body.classList.add("working");
    loader.classList.add("show");

    try {
        const response = await fetch(href, { signal: activeController.signal });
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const content = doc.querySelector(".js-fetch-content");
        const youtubeEmbed = content.querySelector(".js-youtube-embed");
        let youtubeId = null;

        if (!content) {
            return;
        }

        if (youtubeEmbed) {
            youtubeId = youtubeEmbed.dataset.src;
            if (youtubeId) {
                initYoutube(youtubeEmbed, youtubeId);
            }
        }

        fetchDestination.appendChild(content);
        vars.body.classList.add("stop-scrolling");

        if (fetchDestination.children.length > 1) {
            const currentContent = fetchContainer.querySelector(
                ".js-fetch-content:first-child",
            );

            gsap.to(fetchDestination, {
                autoAlpha: 0,
                duration: 0.33,
                ease: "power1.inOut",
                onComplete: () => {
                    currentContent.remove();
                    initContent(fetchDestination);
                    ScrollTrigger.refresh();
                    vars.body.classList.remove("working");
                    loader.classList.remove("show");
                },
            });

            gsap.to(fetchDestination, {
                autoAlpha: 1,
                duration: 0.33,
                delay: 0.4,
                ease: "power1.inOut",
            });
        } else {
            gsap.to(fetchContainer, {
                autoAlpha: 1,
                duration: 0.4,
                ease: "power1.inOut",
                onComplete: () => {
                    initContent(fetchDestination);
                    ScrollTrigger.refresh();
                    vars.body.classList.remove("working");
                    loader.classList.remove("show");
                },
            });

            gsap.to(closeFetchButton, {
                autoAlpha: 1,
                duration: 0.25,
                ease: "power1.inOut",
                delay: 0.4,
            });
        }
    } catch (err) {
        if (err.name === "AbortError") {
            return;
        }

        vars.body.classList.remove("working");
        vars.body.classList.remove("stop-scrolling");
        loader.classList.remove("show");
        console.warn("Something went wrong.", err);
    }
}

function emptyContent(fetchContainer, fetchDestination, closeFetchButton) {
    gsap.to(closeFetchButton, {
        autoAlpha: 0,
        duration: 0.25,
    });

    gsap.to(fetchContainer, {
        autoAlpha: 0,
        duration: 0.4,
        delay: 0.25,
        ease: "power1.inOut",
        onComplete: () => {
            vars.body.classList.remove("working");
            vars.body.classList.remove("stop-scrolling");
            fetchDestination.replaceChildren();
        },
    });
}

const initFetchContent = (fetchContainer, fetchLinks) => {
    const fetchDestination = fetchContainer.querySelector(
        ".js-fetch-destination",
    );
    const closeFetchButton = fetchContainer.querySelector(".js-fetch-close");
    const loader = document.querySelector(".js-loader");

    document.addEventListener("click", (event) => {
        const fetchLinkParent = event.target.closest(".js-fetch");

        if (fetchLinkParent || [...fetchLinks].includes(event.target)) {
            event.preventDefault();

            let contentHref = null;

            if (event.target.href) {
                contentHref = event.target.href;
            } else {
                contentHref = fetchLinkParent.href;
            }

            fetchContent(
                contentHref,
                fetchContainer,
                fetchDestination,
                closeFetchButton,
                loader,
            );
        }

        if (
            event.target === closeFetchButton ||
            event.target === fetchContainer
        ) {
            event.preventDefault();
            emptyContent(fetchContainer, fetchDestination, closeFetchButton);
        }
    });
};

export { initFetchContent as default };
